import { DELETED_EXTERNAL_LIST_LABEL, UNAVAILABLE_EXTERNAL_LIST_LABEL } from '~/constants/externalListStatuses';
import { externalListsClient } from '~/services';
import type { ClientContext } from '~/services/contextClient';
import type { ExternalList } from '~/services/externalLists/externalListsClient.types';
import { chunkArray } from '~/utils/array';

const IDS_PER_REQUEST = 10;

export interface ExternalListsByIds {
    byId: Map<number, ExternalList>;
    failedIds: Set<number>;
}

// Names for ids the caller already holds, kept apart from the lookups that failed so a list the
// API no longer returns can be told from one it could not be asked about.
export const fetchExternalListsByIds = async (context: ClientContext, ids: number[]): Promise<ExternalListsByIds> => {
    const byId = new Map<number, ExternalList>();
    const failedIds = new Set<number>();

    await Promise.all(
        chunkArray([...new Set(ids)], IDS_PER_REQUEST).map(async (batch) => {
            try {
                const response = await externalListsClient.getExternalLists(context, {
                    per_page: batch.length,
                    filter: { ids: batch },
                });
                response.data.data.forEach((externalList) => byId.set(Number(externalList.id), externalList));
            } catch {
                batch.forEach((id) => failedIds.add(id));
            }
        })
    );

    return { byId, failedIds };
};

export const externalListName = ({ byId, failedIds }: ExternalListsByIds, id: number): string =>
    byId.get(id)?.name ?? (failedIds.has(id) ? UNAVAILABLE_EXTERNAL_LIST_LABEL : DELETED_EXTERNAL_LIST_LABEL);
